import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Anchor, Group, Paper, Skeleton, Stack, Text, Tooltip } from '@mantine/core'
import { faArrowUpRightFromSquare, faFloppyDisk } from '@fortawesome/sharp-duotone-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { BillingRole, LabourCode, TimeEntryStatus } from '@api-sdk'
import { DESCRIPTION_MAX, DESCRIPTION_MIN, TimeEntryForm, type TimeEntryFormErrors } from './TimeEntryForm'
import { Button } from '../Common/Button'
import { StatusMessage } from '../Common/StatusMessage'
import { MIN_BOOKABLE_DATE, useTimeEntryOptions } from '../../hooks/useTimeEntryOptions'
import { useCreateTimeEntry } from '../../hooks/useCreateTimeEntry'
import type { TimeEntryFormValues } from '../../types/timeEntry'
import { openExternalUrl } from '../../utils/openExternalUrl'

// Where booked time is reviewed. The same in every environment, so it lives in code;
// VITE_REPLICON_URL can still override it. `||` rather than `??` so an empty value falls back too.
const REPLICON_URL = import.meta.env.VITE_REPLICON_URL || 'https://login.replicon.com/Login.aspx'

// The backend's own per-entry cap. Checked here so an over-long day is caught before the
// round trip; the service rejects it either way. Replicon allows at most 23 hours a day.
const MAX_HOURS = 23

// How long a clean save's "Time entry saved." stays visible — the same as the Contacts tab's
// "Contact added to claim.".
const SAVED_MESSAGE_MS = 4000

// Replicon's names for the role and status a new entry starts on.
const DEFAULT_BILLING_ROLE = '*Default'
const DEFAULT_BILLING_STATUS = 'Not Submitted'

// Today in the yyyy-mm-dd form the native date input expects, in local time —
// toISOString would shift the date for anyone west of UTC.
function todayIsoDate(): string {
  const now = new Date()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const day = `${now.getDate()}`.padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

function emptyValues(): TimeEntryFormValues {
  return {
    timeWorked: '',
    date: todayIsoDate(),
    laborCode: '',
    billingRole: '',
    billingStatus: '',
    description: '',
  }
}

function toHours(timeWorked: number | string): number {
  return typeof timeWorked === 'number' ? timeWorked : Number.parseFloat(timeWorked)
}

// Mirrors what the service requires: Work Package is optional there, and Billing Status is
// accepted but not yet stored, so neither blocks a save.
//
// `today` is passed in rather than read here so the input's max attribute and this check
// can never disagree. Both dates are yyyy-mm-dd, which compares correctly as a string.
function validate(values: TimeEntryFormValues, today: string): TimeEntryFormErrors {
  const errors: TimeEntryFormErrors = {}
  const hours = toHours(values.timeWorked)
  if (!Number.isFinite(hours)) errors.timeWorked = 'Enter the hours worked.'
  else if (hours <= 0) errors.timeWorked = 'Hours must be greater than 0.'
  else if (hours > MAX_HOURS) errors.timeWorked = `Hours cannot exceed ${MAX_HOURS} per entry.`
  if (!values.date) errors.date = 'Select a date.'
  else if (values.date > today) errors.date = 'The date cannot be in the future.'
  // Below this is a year still being typed, which no billing roles exist for.
  else if (values.date < MIN_BOOKABLE_DATE) errors.date = 'Enter a valid date.'
  if (!values.laborCode) errors.laborCode = 'Select a labor code.'
  if (!values.billingRole) errors.billingRole = 'Select a billing role.'
  // Trimmed, because that is what is sent — padding can't make up the minimum.
  const description = values.description.trim()
  if (!description) errors.description = 'Enter a description.'
  else if (description.length < DESCRIPTION_MIN)
    errors.description = `Description must be at least ${DESCRIPTION_MIN} characters.`
  else if (description.length > DESCRIPTION_MAX)
    errors.description = `Description cannot exceed ${DESCRIPTION_MAX} characters.`
  return errors
}

// Replicon returns a code alongside the name on work packages and labour codes; it is
// shown when set because two entries can share a name.
function withCode(name: string, code: string | null | undefined): string {
  return code ? `${name} (${code})` : name
}

function toOptions(items: LabourCode[] | null): { value: string; label: string }[] {
  return (items ?? []).map((item) => ({ value: item.id, label: withCode(item.name, item.code) }))
}

// MC1 asks before an approved entry is booked, because approving submits the time
// permanently. Matched on the status name — the ids are Replicon urns, so they aren't
// stable enough to hardcode.
const APPROVED_STATUS = 'approved'

function isApprovedStatus(statuses: TimeEntryStatus[] | null, id: string): boolean {
  if (!id) return false
  return (statuses ?? []).some((status) => status.id === id && status.name.trim().toLowerCase() === APPROVED_STATUS)
}

// Billing roles and billing statuses are both plain id/name lookups.
function toNameOptions(items: (BillingRole | TimeEntryStatus)[] | null): { value: string; label: string }[] {
  return (items ?? []).map((item) => ({ value: item.id, label: item.name }))
}

// The id of the option with this name, ignoring case and padding, or undefined when there is none.
function idNamed(items: { id: string; name: string }[] | null, name: string): string | undefined {
  const wanted = name.toLowerCase()
  return (items ?? []).find((item) => item.name.trim().toLowerCase() === wanted)?.id
}

// The option a lookup starts on: the one with the given name when Replicon sends it, the first otherwise.
function defaultId(items: { id: string; name: string }[] | null, preferredName?: string): string {
  const named = preferredName ? idNamed(items, preferredName) : undefined
  return named ?? items?.[0]?.id ?? ''
}

export interface TimeEntryTabProps {
  claimNumber: string
}

export function TimeEntryTab({ claimNumber }: Readonly<TimeEntryTabProps>) {
  const [values, setValues] = useState<TimeEntryFormValues>(emptyValues)
  // Which fields the user has been in. An error only shows once its field has been
  // touched, so a form just opened is not a list of complaints.
  const [touched, setTouched] = useState<Partial<Record<keyof TimeEntryFormValues, boolean>>>({})
  // Booking an Approved entry asks first, inline rather than via window.confirm, which is
  // unreliable inside the Outlook add-in iframe.
  const [confirmingApproved, setConfirmingApproved] = useState(false)
  const saveErrorRef = useRef<HTMLParagraphElement>(null)
  const confirmRef = useRef<HTMLDivElement>(null)
  const saveButtonRef = useRef<HTMLButtonElement>(null)
  const savedRef = useRef<HTMLDivElement>(null)
  const formHeadingRef = useRef<HTMLParagraphElement>(null)
  // Time is never booked ahead of the day it was worked.
  const today = todayIsoDate()
  // Time is booked against the claim's first work package; billing roles follow the date.
  const { workPackages, labourCodes, billingRoles, billingStatuses, selectedWorkPackageId, retry } =
    useTimeEntryOptions(claimNumber, values.date, today)
  const { saving, error: saveError, saved, save, reset } = useCreateTimeEntry(claimNumber)
  const workPackage = workPackages.data?.[0]

  // What the form shows and books. A lookup the user hasn't picked ('' in `values`) shows its
  // default, derived from the options rather than stored, so it follows a refetch (roles after
  // a date change) and Cancel brings it back.
  const formValues: TimeEntryFormValues = {
    ...values,
    laborCode: values.laborCode || defaultId(labourCodes.data),
    billingRole: values.billingRole || defaultId(billingRoles.data, DEFAULT_BILLING_ROLE),
    // Optional, and its id submits the entry, so it never falls back to an arbitrary status: with no
    // "Not Submitted" in the list it stays empty, which saves the entry as Not Submitted.
    billingStatus: values.billingStatus || (idNamed(billingStatuses.data, DEFAULT_BILLING_STATUS) ?? ''),
  }

  // Re-checked on every keystroke, so Save turns on the moment the entry is complete.
  const fieldErrors = validate(formValues, today)
  const isValid = Object.keys(fieldErrors).length === 0
  const shownErrors: TimeEntryFormErrors = {
    timeWorked: touched.timeWorked ? fieldErrors.timeWorked : undefined,
    date: touched.date ? fieldErrors.date : undefined,
    laborCode: touched.laborCode ? fieldErrors.laborCode : undefined,
    billingRole: touched.billingRole ? fieldErrors.billingRole : undefined,
    description: touched.description ? fieldErrors.description : undefined,
  }

  // A rejection is the one error the user cannot see coming, so focus moves to it.
  useEffect(() => {
    if (saveError) saveErrorRef.current?.focus()
  }, [saveError])

  // The form clears and Save turns off once an entry is booked, so focus would otherwise be
  // lost to the document; it moves to the confirmation instead.
  useEffect(() => {
    if (saved) savedRef.current?.focus()
  }, [saved])

  // A clean save's confirmation clears itself. One with warnings stays until the user moves on,
  // so a submission Replicon didn't accept can't slip past unread. The confirmation may still hold
  // focus when it goes, so focus moves to the start of the next entry rather than falling to the
  // document — but only then: a user who has already moved on keeps their place.
  useEffect(() => {
    if (!saved || saved.warnings.length > 0) return
    const timeoutId = globalThis.setTimeout(() => {
      const hadFocus = savedRef.current?.contains(globalThis.document.activeElement) ?? false
      reset()
      if (hadFocus) formHeadingRef.current?.focus()
    }, SAVED_MESSAGE_MS)
    return () => globalThis.clearTimeout(timeoutId)
  }, [saved, reset])

  // The prompt is the next thing to answer, so focus moves to it.
  useEffect(() => {
    if (confirmingApproved) confirmRef.current?.focus()
  }, [confirmingApproved])

  const handleChange = <K extends keyof TimeEntryFormValues>(field: K, value: TimeEntryFormValues[K]) => {
    // Editing starts the next entry, so the previous result stops applying.
    if (saved || saveError) reset()
    // The prompt named the values as they were, so an edit withdraws it — including a
    // change of status away from Approved.
    setConfirmingApproved(false)
    setTouched((current) => ({ ...current, [field]: true }))
    setValues((current) => {
      // Billing roles are read from the timesheet covering the date, so a role picked for
      // one day may not exist on another.
      if (field === 'date' && value !== current.date) {
        return { ...current, date: value as string, billingRole: '' }
      }
      return { ...current, [field]: value }
    })
  }

  const bookEntry = async () => {
    const booked = await save({
      date: values.date,
      timeWorked: toHours(values.timeWorked),
      workPackageId: selectedWorkPackageId,
      labourCodeId: formValues.laborCode,
      billingRoleId: formValues.billingRole,
      // The "Billing Status" control holds a time entry status id; the hook sends it as
      // statusId, which is what actually submits the entry for approval.
      statusId: formValues.billingStatus,
      description: values.description.trim(),
    })

    // Booked time can't be edited from here, so the form clears for the next entry. The
    // date stays: several entries for one day are the common case.
    if (booked) {
      setValues({ ...emptyValues(), date: values.date })
      setTouched({})
    }
  }

  const handleSave = async () => {
    if (!isValid) return

    // Approved submits the time permanently, so it is confirmed before anything is sent.
    if (isApprovedStatus(billingStatuses.data, formValues.billingStatus)) {
      setConfirmingApproved(true)
      return
    }
    await bookEntry()
  }

  // The prompt holding focus unmounts, so focus goes back to the Save that raised it. Flushed
  // first because Save stays disabled until the prompt has closed, and can't take focus before.
  const handleDeclineApproved = () => {
    flushSync(() => setConfirmingApproved(false))
    saveButtonRef.current?.focus()
  }

  const handleConfirmApproved = async () => {
    setConfirmingApproved(false)
    await bookEntry()
  }

  // Clears the form back to a fresh entry, dropping the last result and an open prompt with it.
  const handleCancel = () => {
    reset()
    setConfirmingApproved(false)
    setValues(emptyValues())
    setTouched({})
  }

  // Nothing to drop until the form differs from a fresh one, or a save result is still showing.
  const fresh = emptyValues()
  const hasChanges = (Object.keys(fresh) as (keyof TimeEntryFormValues)[]).some((key) => values[key] !== fresh[key])
  const canCancel = (hasChanges || saved !== null || saveError !== null) && !saving && !confirmingApproved

  const hasLoadError = Boolean(workPackages.error ?? labourCodes.error ?? billingRoles.error ?? billingStatuses.error)
  const noWorkPackage = selectedWorkPackageId === ''

  const viewAllLink = (
    <Group justify="flex-end" wrap="nowrap">
      <Tooltip label="View all time entered in Replicon" withArrow>
        <Anchor
          component="button"
          type="button"
          fz="xs"
          underline="always"
          c="var(--header-link-color)"
          onClick={() => openExternalUrl(REPLICON_URL)}
          style={{ whiteSpace: 'nowrap' }}
        >
          <Group component="span" gap={6} wrap="nowrap" align="center" display="inline-flex">
            View all Time Entered
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </Group>
        </Anchor>
      </Tooltip>
    </Group>
  )

  // Nothing is shown until the work packages are known — including the first render, before the
  // fetch has even started — so the form never appears only to be swapped for the message below.
  // A failed load falls through to the form, which carries the error and Retry.
  if (!workPackages.error && workPackages.data === null) {
    return (
      <Stack gap="xs">
        {viewAllLink}
        <Skeleton height={240} radius="sm" aria-label="Loading time entry" />
      </Stack>
    )
  }

  // Loaded and empty — including a claim Replicon has no project for — so there is nothing to book
  // time against.
  if (workPackages.data?.length === 0) {
    return (
      <Stack gap="xs">
        {viewAllLink}
        <StatusMessage status="error" ta="center">
          Work package is not available
        </StatusMessage>
      </Stack>
    )
  }

  return (
    <Stack gap="xs">
      {viewAllLink}

      <Paper p="sm" radius="sm" withBorder bg="var(--mantine-color-body)">
        <Stack gap="sm">
          <Group justify="space-between" align="center" wrap="nowrap">
            {/* Focusable from code only, for when the save confirmation clears itself. */}
            <Text ref={formHeadingRef} tabIndex={-1} fw={600} fz="sm">
              Create a Time Entry
            </Text>
            {/* Save/Cancel sit up here, as on a task card, so they stay in view however long
                the form grows. */}
            <Group gap={4} wrap="nowrap">
              {hasLoadError && <Button label="Retry" size="xs" variant="outline" onClick={retry} />}
              <Button label="Cancel" size="xs" variant="outline" disabled={!canCancel} onClick={handleCancel} />
              <Button
                ref={saveButtonRef}
                label="Save"
                size="xs"
                variant="filled"
                loading={saving}
                disabled={!isValid || confirmingApproved}
                onClick={handleSave}
                rightSection={<FontAwesomeIcon icon={faFloppyDisk} />}
              />
            </Group>
          </Group>

          {/* Shown beside the Save that raised it — at the foot of a long form it would be
              off screen in the narrow pane. */}
          {confirmingApproved && (
            <Paper ref={confirmRef} tabIndex={-1} p="xs" withBorder role="alertdialog" aria-label="Confirm time entry">
              <Stack gap="xs">
                <Text fz="xs" fw={600}>
                  Are you sure you want to submit this time entry?
                </Text>
                <Text fz="xs" c="dimmed">
                  Approving permanently submits the time entry. Please confirm before submitting.
                </Text>
                <Group justify="flex-end" gap="xs" wrap="nowrap">
                  <Button label="No" size="xs" variant="outline" onClick={handleDeclineApproved} />
                  <Button label="Yes" size="xs" variant="filled" loading={saving} onClick={handleConfirmApproved} />
                </Group>
              </Stack>
            </Paper>
          )}

          <TimeEntryForm
            values={formValues}
            workPackage={{
              name: workPackage ? withCode(workPackage.name, workPackage.code) : null,
              error: workPackages.error ?? undefined,
            }}
            laborCode={{
              options: toOptions(labourCodes.data),
              loading: labourCodes.loading,
              error: labourCodes.error ?? shownErrors.laborCode,
              disabled: noWorkPackage,
              required: true,
              // A work package can carry many labour codes.
              searchable: true,
              placeholder: 'Select',
            }}
            billingRole={{
              options: toNameOptions(billingRoles.data),
              loading: billingRoles.loading,
              error: billingRoles.error ?? shownErrors.billingRole,
              required: true,
              placeholder: 'Select',
            }}
            // Optional: the backend accepts the selected status id and echoes it back,
            // but a saved entry takes its real status from the approval workflow.
            billingStatus={{
              options: toNameOptions(billingStatuses.data),
              loading: billingStatuses.loading,
              error: billingStatuses.error ?? undefined,
              placeholder: 'Select a Billing Status',
            }}
            minDate={MIN_BOOKABLE_DATE}
            maxDate={today}
            onChange={handleChange}
            errors={shownErrors}
          />

          {/* Polite: the entry is booked, so it can wait for a pause in speech. */}
          {/* StatusMessage carries the theme's light and dark colours. Each line is its own
              polite live region — the entry is booked, so none of them needs to interrupt —
              and the wrapper only takes focus, so the regions are never nested. */}
          {saved && (
            <Stack ref={savedRef} tabIndex={-1} gap={2}>
              {/* The status comes from the response, never from what was asked for. Asking for
                  Approved normally reads "Submitting": Replicon settles a submission after the
                  response, and nothing reports the outcome later, so this is the last the user
                  hears of it. */}
              <StatusMessage status="success" fz="xs" aria-live="polite">
                Time entry saved.
                {saved.entryId ? ` ID: ${saved.entryId}.` : ''}
                {saved.statusName ? ` Status: ${saved.statusName}.` : ''}
              </StatusMessage>
              {/* The time is booked either way, but each of these is something that went wrong
                  after the save — a submission Replicon would not accept, or one it never
                  confirmed — so they read as problems rather than notes. */}
              {saved.warnings.map((warning) => (
                <StatusMessage key={warning} status="error" role="status" fz="xs" aria-live="polite">
                  {warning}
                </StatusMessage>
              ))}
            </Stack>
          )}

          {saveError && (
            <StatusMessage ref={saveErrorRef} tabIndex={-1} status="error" fz="xs">
              {saveError}
            </StatusMessage>
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}
